import { useCallback } from "react";

import { useUserOptional } from "@/auth/UserContext";
import { useTheme } from "@/context/ThemeContext";
import { useUserPreferences } from "@/context/UserPreferencesContext";

/**
 * Applies UI preferences requested by Kai (via the `apply_preferences` SSE
 * event) onto the app's existing contexts and localStorage. Unknown keys or
 * invalid values are ignored so a malformed payload cannot break the stream.
 */
export function useApplyKaiPreferences() {
  const { setPreference, setDensity } = useTheme();
  const {
    setShowLocodes,
    setShowCarrierLogos,
    setShowWeekNumbers,
    setHideSensitiveData,
    setScopeToMyBookings,
    setFeedGroupBy,
    updatePreferences,
  } = useUserPreferences();
  const userCtx = useUserOptional();
  const isSalesPerson = userCtx?.isSalesPerson ?? false;
  const isClient = userCtx?.isClient ?? false;

  return useCallback(
    (preferences: Record<string, unknown> | undefined | null) => {
      if (!preferences || typeof preferences !== "object") return;

      const theme = preferences.theme;
      if (theme === "light" || theme === "dark" || theme === "system") {
        setPreference(theme);
      }

      const density = preferences.density;
      if (density === "comfortable" || density === "compact") {
        setDensity(density);
      }

      if (typeof preferences.show_locodes === "boolean") {
        setShowLocodes(preferences.show_locodes);
      }
      if (typeof preferences.show_carrier_logos === "boolean") {
        setShowCarrierLogos(preferences.show_carrier_logos);
      }
      if (typeof preferences.show_week_numbers === "boolean") {
        setShowWeekNumbers(preferences.show_week_numbers);
      }
      if (typeof preferences.hide_sensitive_data === "boolean") {
        setHideSensitiveData(preferences.hide_sensitive_data);
      }
      if (typeof preferences.scope_to_my_bookings === "boolean") {
        // SalesPerson scope is authorization, not a display pref.
        if (!isSalesPerson && !isClient) {
          setScopeToMyBookings(preferences.scope_to_my_bookings);
        }
      }

      const feedGroupBy = preferences.feed_group_by;
      if (
        feedGroupBy === "date" ||
        feedGroupBy === "none" ||
        feedGroupBy === "booking" ||
        feedGroupBy === "customer" ||
        feedGroupBy === "sales_person" ||
        feedGroupBy === "operator" ||
        feedGroupBy === "agent" ||
        feedGroupBy === "carrier" ||
        feedGroupBy === "lane" ||
        feedGroupBy === "event_type"
      ) {
        setFeedGroupBy(feedGroupBy);
      }

      const hiddenGroups = preferences.feed_hidden_groups;
      if (Array.isArray(hiddenGroups)) {
        const keys = hiddenGroups.filter(
          (value): value is string => typeof value === "string" && value.length > 0,
        );
        updatePreferences({ feedHiddenGroups: keys });
      }
    },
    [
      setPreference,
      setDensity,
      setShowLocodes,
      setShowCarrierLogos,
      setShowWeekNumbers,
      setHideSensitiveData,
      setScopeToMyBookings,
      setFeedGroupBy,
      updatePreferences,
      isSalesPerson,
      isClient,
    ],
  );
}
